// Glyph atlas for every VFX sprite (docs/ART-DIRECTION.md §7): printable ASCII plus the few symbols the floaters
// use, and the particle shapes (spark dot, pixel, pixel-sort bar, paper leaflet). Drawn once into a canvas as a
// white alpha mask; sprites tint it. Cells are square, so text advances by less than a cell width.

import { CanvasTexture, LinearFilter } from 'three'
import { InvariantViolation } from '@taxwar/net'
import { VfxAssetError } from './errors'

export const ATLAS_COLUMNS = 16
export const ATLAS_ROWS = 8
const CELL_PX = 32
const GLYPH_FONT = `bold ${Math.round(CELL_PX * 0.8)}px ui-monospace, Menlo, Consolas, monospace`

const FIRST_PRINTABLE = 0x20
const LAST_PRINTABLE = 0x7e
/** Non-ASCII characters the floaters print. */
const EXTRA_CHARACTERS = ['✓', '−'] as const

/** Particle shapes, drawn after the characters. */
export type ShapeCell = 'spark' | 'pixel' | 'bar' | 'paper'
const SHAPE_ORDER: readonly ShapeCell[] = ['spark', 'pixel', 'bar', 'paper']

const PIXEL_MARGIN = 0.2
const BAR_HEIGHT = 0.3
const PAPER_WIDTH = 0.62
const PAPER_HEIGHT = 0.8
const PAPER_LINE_WIDTH = 2
const PAPER_TEXT_LINES = 3
const PAPER_TEXT_INSET = 0.12

type ShapePainter = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number) => void

const SHAPE_PAINTERS: Readonly<Record<ShapeCell, ShapePainter>> = {
  spark: (ctx, x, y, size) => {
    const half = size / 2
    const gradient = ctx.createRadialGradient(x + half, y + half, 0, x + half, y + half, half)
    gradient.addColorStop(0, 'rgba(255,255,255,1)')
    gradient.addColorStop(0.35, 'rgba(255,255,255,0.8)')
    gradient.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = gradient
    ctx.fillRect(x, y, size, size)
  },
  pixel: (ctx, x, y, size) => {
    const margin = size * PIXEL_MARGIN
    ctx.fillRect(x + margin, y + margin, size - 2 * margin, size - 2 * margin)
  },
  bar: (ctx, x, y, size) => {
    const height = size * BAR_HEIGHT
    ctx.fillRect(x, y + (size - height) / 2, size, height)
  },
  paper: (ctx, x, y, size) => {
    const width = size * PAPER_WIDTH
    const height = size * PAPER_HEIGHT
    const left = x + (size - width) / 2
    const top = y + (size - height) / 2
    ctx.lineWidth = PAPER_LINE_WIDTH
    ctx.strokeRect(left, top, width, height)
    const inset = width * PAPER_TEXT_INSET
    for (let line = 1; line <= PAPER_TEXT_LINES; line++) {
      const lineY = top + (height * line) / (PAPER_TEXT_LINES + 1)
      ctx.fillRect(left + inset, lineY, width - 2 * inset, PAPER_LINE_WIDTH)
    }
  },
}

export interface GlyphAtlas {
  readonly texture: CanvasTexture
  /** Atlas cell of a printable character; throws for characters the atlas does not hold. */
  cellOfChar(charCode: number): number
  cellOfShape(shape: ShapeCell): number
}

export function createGlyphAtlas(): GlyphAtlas {
  const characters: number[] = []
  for (let code = FIRST_PRINTABLE; code <= LAST_PRINTABLE; code++) characters.push(code)
  for (const extra of EXTRA_CHARACTERS) characters.push(extra.charCodeAt(0))
  if (characters.length + SHAPE_ORDER.length > ATLAS_COLUMNS * ATLAS_ROWS) throw new InvariantViolation('VFX glyph atlas has more cells than its grid')

  const canvas = document.createElement('canvas')
  canvas.width = ATLAS_COLUMNS * CELL_PX
  canvas.height = ATLAS_ROWS * CELL_PX
  const ctx = canvas.getContext('2d')
  if (ctx === null) throw new VfxAssetError('no 2D canvas context for the VFX glyph atlas')
  ctx.fillStyle = '#ffffff'
  ctx.strokeStyle = '#ffffff'
  ctx.font = GLYPH_FONT
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  const charCells = new Map<number, number>()
  let cell = 0
  for (const code of characters) {
    const x = (cell % ATLAS_COLUMNS) * CELL_PX
    const y = Math.floor(cell / ATLAS_COLUMNS) * CELL_PX
    ctx.fillText(String.fromCharCode(code), x + CELL_PX / 2, y + CELL_PX / 2)
    charCells.set(code, cell)
    cell++
  }
  const firstShapeCell = cell
  for (const shape of SHAPE_ORDER) {
    SHAPE_PAINTERS[shape](ctx, (cell % ATLAS_COLUMNS) * CELL_PX, Math.floor(cell / ATLAS_COLUMNS) * CELL_PX, CELL_PX)
    cell++
  }

  const texture = new CanvasTexture(canvas)
  texture.minFilter = LinearFilter
  texture.magFilter = LinearFilter
  texture.generateMipmaps = false

  return {
    texture,
    cellOfChar(charCode) {
      const found = charCells.get(charCode)
      if (found === undefined) throw new InvariantViolation(`VFX glyph atlas has no character U+${charCode.toString(16)}`)
      return found
    },
    cellOfShape(shape) {
      return firstShapeCell + SHAPE_ORDER.indexOf(shape)
    },
  }
}
